/**
 * A grid view, as somebody sent a link to it sees it.
 *
 * Outside the shell and outside the session, like the form page: a shared view
 * exists to be read by people who have no account here.
 *
 * The link does not unfurl. Without server rendering there is no `<title>` or
 * `og:*` per view, so a link pasted into a chat shows the application's own
 * shell -- a cost this rewrite accepted deliberately, and the one page where it
 * is most visible. If it ever matters, a small Django view serving `index.html`
 * with injected meta tags for this prefix and `/form/` is the whole fix.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { PublicGrid } from '@/features/database/public/components/public-grid'
import { publicRowFetcher } from '@/features/database/public/fetcher'
import {
  usePublicFieldOptions,
  usePublicView,
  type PublicViewInfo,
} from '@/features/database/public/queries'
import { settingsQuery } from '@/features/settings/queries'
import { SearchBox } from '@/shared/components/search-box'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/shared/ui/empty'
import { Skeleton } from '@/shared/ui/skeleton'

const ROUTE = '/public/grid/$slug'
const ROW_HEIGHT = 33

export function PublicViewPage() {
  const { slug } = useParams({ from: ROUTE })
  const view = usePublicView(slug)

  if (view.isPending) return <PublicSkeleton />
  if (view.data === undefined) return <NoSuchView />

  return <Shared slug={slug} info={view.data} />
}

function Shared({ slug, info }: { slug: string; info: PublicViewInfo }) {
  const { t } = useTranslation()
  const [term, setTerm] = useState('')
  const settings = useQuery(settingsQuery())
  const fieldOptions = usePublicFieldOptions(slug)
  // Built once per slug: a fetcher swapped under a live window would mean the
  // answers in flight came from an address other than the one handling them.
  const [fetchRows] = useState(() => publicRowFetcher(slug))

  return (
    <div className="flex h-dvh flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b px-4 py-2">
        <h1 className="min-w-0 flex-1 truncate font-medium">{info.view.name}</h1>
        <SearchBox
          term={term}
          onSearch={setTerm}
          label={t('view.search')}
          className="w-56"
        />
      </header>

      {fieldOptions.isPending ? (
        <Skeleton className="m-4 flex-1" />
      ) : (
        <div className="min-h-0 flex-1">
          <PublicGrid
            slug={slug}
            // `id` is the slug: that is what the serialiser reports, and it is
            // the identity the window's signature and the fetcher both use.
            //
            // The sortings and group bys are cast because their `view` is a
            // slug here rather than an id -- the public serialisers say so
            // deliberately -- and nothing that reads them reads that field.
            view={{
              id: info.view.id,
              sortings: info.view.sortings as never,
              group_bys: info.view.group_bys as never,
            }}
            fields={info.fields}
            fieldOptions={fieldOptions.data ?? {}}
            rowHeight={ROW_HEIGHT}
            search={{
              term,
              mode:
                settings.data?.frontend.pg_fulltext_search_enabled === true
                  ? 'full-text-with-count'
                  : 'compat',
            }}
            fetchRows={fetchRows}
          />
        </div>
      )}
    </div>
  )
}

/**
 * Unshared, deleted, never a link at all -- or one of the few still holding a
 * password from a version of this application that could set one.
 *
 * Deliberately one answer. Telling somebody holding a guessed slug that a view
 * exists but is no longer shared is telling them something, and the password
 * case is now indistinguishable in practice: nothing here can open one, so a
 * page saying "this needs a password" would be offering a door with no key.
 */
function NoSuchView() {
  const { t } = useTranslation()

  return (
    <Empty className="min-h-dvh">
      <EmptyHeader>
        <EmptyTitle>{t('publicView.goneTitle')}</EmptyTitle>
        <EmptyDescription>{t('publicView.goneWhy')}</EmptyDescription>
      </EmptyHeader>
    </Empty>
  )
}

function PublicSkeleton() {
  return (
    <div className="space-y-2 p-4">
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  )
}
